import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { UtilityService } from "../../configuration/services/utility.service";

@Component({
	selector: "app-avatar",
	styleUrls: ["./avatar.component.scss"],
	template: `
		<ion-avatar
			[ngStyle]="{
				'width.px': size,
				'max-width.px': size,
				'height.px': size,
				'max-height.px': size,
			}"
		>
			<span
				*ngIf="initials; else image"
				class="initials"
				[style.background-color]="backgroundColor"
				[style.font-size.px]="initialsFontSize"
			>
				{{ initials }}
			</span>
			<ng-template #image>
				<ion-img [src]="url" (ionError)="onError()"></ion-img>
			</ng-template>
		</ion-avatar>
	`,
})
export class AvatarComponent implements OnInit, OnChanges {
	private static readonly FallbackProfileImage = "../../assets/images/default_user.jpg";

	private static readonly Palette = [
		"#053F45",
		"#074F57",
		"#1B636C",
		"#0E6B73",
		"#1A6B7A",
		"#256F7A",
		"#2A7A75",
		"#2F5F66",
		"#3A6B5A",
		"#3D8B7E",
		"#4A6B52",
		"#4A7A6B",
	] as const;

	constructor(private utilityService: UtilityService) {}

	@Input() file: string;
	@Input() name: string;
	@Input() userId: string;
	@Input() size = 65;

	url: string | null = null;
	initials: string | null = null;
	backgroundColor: string | null = null;

	get initialsFontSize(): number {
		return Math.round(this.size * 0.38);
	}

	ngOnInit() {
		void this.refresh();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["file"] || changes["name"] || changes["userId"]) {
			void this.refresh();
		}
	}

	onError() {
		if (this.tryShowInitials()) {
			return;
		}

		this.initials = null;
		this.backgroundColor = null;
		this.url = AvatarComponent.FallbackProfileImage;
	}

	private tryShowInitials(): boolean {
		const initials = this.initialsFromName(this.name);
		if (initials == null) {
			return false;
		}

		const seed = this.userId?.trim() || this.name.trim();
		this.initials = initials;
		this.backgroundColor = this.colorFor(seed);
		this.url = null;
		return true;
	}

	private initialsFromName(name: string | null | undefined): string | null {
		if (name == null) {
			return null;
		}

		const parts = name.trim().split(/\s+/).filter(Boolean);
		if (parts.length === 0) {
			return null;
		}

		const firstLetter = (part: string): string | null => {
			const match = part.match(/\p{L}/u);
			return match == null ? null : match[0].toLocaleUpperCase();
		};

		if (parts.length === 1) {
			return firstLetter(parts[0]);
		}

		const first = firstLetter(parts[0]);
		const last = firstLetter(parts[parts.length - 1]);
		if (first == null || last == null) {
			return first ?? last;
		}

		return first + last;
	}

	private colorFor(seed: string): string {
		let hash = 0;
		for (let i = 0; i < seed.length; i++) {
			hash = (Math.imul(31, hash) + seed.charCodeAt(i)) | 0;
		}

		return AvatarComponent.Palette[(hash >>> 0) % AvatarComponent.Palette.length];
	}

	private async refresh(): Promise<void> {
		const file = this.file;
		const hasFile = file != null && file !== "";

		if (!hasFile) {
			if (this.tryShowInitials()) {
				return;
			}

			this.initials = null;
			this.backgroundColor = null;
			this.url = AvatarComponent.FallbackProfileImage;
			return;
		}

		this.initials = null;
		this.backgroundColor = null;
		const url = await this.utilityService.getImageUrl(file, "profile");

		if (this.file !== file) {
			return;
		}

		this.url = url;
	}
}
